// Form 9 - Alloted Assessors For Assessment
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Alert,
    CircularProgress,
    Button,
    Avatar,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Visibility as ViewIcon,
    ArrowBack as BackIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import DoApiService from '../../../services/DoApiService';
import { decryptAppId, decryptText, encryptText } from '../../../utils/encryption';
import PageTitle from '../../../components/PageTitle';

const Form9 = () => {
    // State management
    const [assessorsData, setAssessorsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [labDetails, setLabDetails] = useState(null);

    // Router hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get encrypted parameters from URL
    const encryptedAppId = searchParams.get("appId");
    const encryptedOrgId = searchParams.get("orgId");
    const encryptedLabTypeId = searchParams.get("labTypeId");
    const encryptedType = searchParams.get("type");

    // Decrypt parameters
    const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
    const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
    const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;
    const type = encryptedType ? decryptText(encryptedType) : null;



    // Load assessor allotment data using mode 5
    const loadAssessorAllotmentData = useCallback(async () => {
        if (!appId) {
            setError("Invalid application ID. Please check the URL.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Call API to get assessor allotment details (mode 5 - AssessorAllotment_New)
            const response = await DoApiService.getAssessorAllotmentDetails9({
                mode: 64,
                appId: parseFloat(appId),
            });

            if (response.success) {
                setAssessorsData(response.data || []);
            } else {
                throw new Error(response.message || "Failed to load assessor allotment details");
            }
        } catch (err) {
            setError(`Error loading assessor allotment details: ${err.message}`);
            console.error("Load assessor allotment error:", err);
            // Set empty data on error
            setAssessorsData([]);
        } finally {
            setLoading(false);
        }
    }, [appId]);

    // Load lab details for header display
    const loadLabDetails = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await DoApiService.getCABDetails({
                appId: parseFloat(appId),
            });

            if (response.success) {
                setLabDetails(response.data);
            }
        } catch (err) {
            console.error("Load lab details error:", err);
        }
    }, [appId]);

    // Load data when component mounts
    useEffect(() => {
        Promise.all([
            loadAssessorAllotmentData(),
            loadLabDetails()
        ]);
    }, [loadAssessorAllotmentData, loadLabDetails]);

    // Handle back navigation
    const handleGoBack = () => {
        navigate(-1);
    };

    // Handle view profile action
    const handleViewProfile = (assessor) => {

        navigate(`/main/assessor/assessor-profile?AssessorId=${encryptText(assessor.allotedToId)}&type=${encryptText(type)}`);
    };
    // Get capacity color for chip display
    const getCapacityColor = (capacity) => {
        const colors = {
            'Lead Assessor and Technical Assessor': 'primary',
            'Observer': 'secondary',
            'Technical Assessor': 'success',
            'Technical Expert': 'warning',
            'Team Leader': 'error'
        };
        return colors[capacity] || 'default';
    };

    // Get status color for chip display
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'success',
            'Inactive': 'error',
            'Pending': 'warning',
            'Confirmed': 'primary'
        };
        return colors[status] || 'default';
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50vh'
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading Form 9 - Assessor Allotment Details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    onClick={handleGoBack}
                    startIcon={<BackIcon />}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <>
        <PageTitle title="NABL | Form 9" />
        <Box sx={{ p: 2, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
                    color: "white",
                    borderRadius: 2,
                    mb: 3,
                }}
            >
                <Box sx={{ px: 3, py: 3 }}>
                    {/* Breadcrumbs */}
                    <Breadcrumbs
                        sx={{
                            mb: 2,
                            "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
                        }}
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleGoBack}
                            sx={{
                                textDecoration: "none",
                                color: "rgba(255,255,255,0.8)",
                                "&:hover": { color: "white" },
                            }}
                        >
                            Dashboard
                        </Link>
                        <Typography variant="body2" sx={{ color: "white" }}>
                            Form 9
                        </Typography>
                    </Breadcrumbs>

                    {/* Header Content */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    mr: 2,
                                    width: 56,
                                    height: 56,
                                }}
                            >
                                <AssignmentIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Form 9
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    {labDetails?.laboratoryName || 'Laboratory Assessment'}
                                    {labDetails?.applicationNo && (
                                        <Chip
                                            label={`App No: ${labDetails.applicationNo}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                ml: 1,
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.7)",
                                            }}
                                        />
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<BackIcon />}
                            onClick={handleGoBack}
                            sx={{
                                borderColor: "rgba(255,255,255,0.3)",
                                color: "white",
                                "&:hover": {
                                    borderColor: "white",
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                },
                            }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Form Content */}
            <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Alloted Assessors For Assessment
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Sub:</strong> Main - Assessment of your CAB in accordance with ISO 15189:2022
                        </Typography>
                        <Typography variant="body2">
                            <strong>Assessment Type:</strong> Main Assessment (FA) /
                            <strong> Assessment Platform:</strong> App Based /
                            <strong> Assessment Mode:</strong> Onsite Assessment
                        </Typography>
                    </Alert>

                    {/* Important Notes */}
                    <Box sx={{ mb: 3, p: 3, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#856404' }}>
                            Note:-
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, listStyleType: 'disc' }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                                I will appreciate if you contact with the team members, arrange travel tickets & make local arrangements (transport and accommodation)
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                                Other payments like honorarium, local allowance etc. shall be paid by NABL to the above team members.
                                Kindly note that CAB is not required to make any payments directly to the team members.
                                The expenditure incurred for this assessment will be charged to the CAB as per NABL rules.
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                                Assessment team is requested to review all the documents and records supplied by NABL and CAB in advance
                                and in Case of gross anomalies, please inform NABL
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ lineHeight: 1.6 }}>
                                Laboratory is required to provide the Wifi facility to the assessment team members during the assessment.
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Assessment Schedule Table */}
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                    <TableContainer component={Paper} sx={{ maxHeight: '600px', overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        textAlign: 'center',
                                        width: 80
                                    }}>
                                        View Profile
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        minWidth: 200
                                    }}>
                                        Assessment Details
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        minWidth: 120
                                    }}>
                                        Assessor Details
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        minWidth: 120
                                    }}>
                                        Organisation Details
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        minWidth: 100
                                    }}>
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assessorsData.map((row, index) => (
                                    <TableRow
                                        key={row.id || index}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#fafafa'
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f0f8ff'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {index + 1} <br />
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleViewProfile(row)}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: 'primary.light',
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                                    {row.laboratoryName || labDetails?.laboratoryName || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Applicaiton No.: {row.applicationNo || 'N/A'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                                        From: {formatDate(row.fromDate || row.startDate)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                                        To: {formatDate(row.toDate || row.endDate)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                                Name: {row.assessor || row.assessorName || 'N/A'}
                                            </Typography>
                                            <Chip
                                                label={row.capacity || row.assessorCapacity || 'N/A'}
                                                size="small"
                                                color={getCapacityColor(row.capacity || row.assessorCapacity)}
                                                variant="filled"
                                            />
                                            <Typography variant="body2">
                                                Expertise: {row.fieldOfExpertise || row.expertise || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2">
                                                Location: {row.assessorLocation || row.location || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    {row.organisation || row.organization || 'N/A'}
                                                </Typography>
                                                <Chip label={row.contact || row.contactNumber || row.mobile || 'N/A'} size="small" variant='outlined' color='primary'
                                                ></Chip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status || 'Active'}
                                                    size="small"
                                                    color={getStatusColor(row.status)}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                    </TableRow>
                                ))}

                                {assessorsData.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No assessor allotment data found for this application
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
        </>
    );
};

export default Form9;